import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationPanelService } from '@core/services';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { CrudOperation } from '@shared/models';
import {
  AppNotificationService,
  HeaderService,
  ModalService,
} from '@shared/services';
import { difference } from 'lodash-es';
import { QueryOptions } from 'odata-query';
import { forkJoin, from, mergeMap, toArray } from 'rxjs';
import {
  Client,
  ClientWithAssignments,
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';
import { ClientService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-clients-page',
  templateUrl: 'clients-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientsPageComponent implements OnInit {
  private readonly clientService = inject(ClientService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);
  private readonly modalService = inject(ModalService);
  private readonly notificationPanelService = inject(NotificationPanelService);

  public clients: WritableSignal<Collection<Client>> = signal({
    total: 0,
    items: [],
  });

  public query: Partial<QueryOptions<Client>> = {
    count: true,
    top: 10,
    skip: 0,
  };

  private clientTitle = this.translateService.instant(
    'admin.dashboard.clientsTitle'
  );

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.fetchClients();
  }

  create(
    clientAssignmentsGridItem: GridItemCreateEvent<ClientWithAssignments>
  ): void {
    let isSuccess = false;

    this.clientService.create(clientAssignmentsGridItem.item).subscribe(() => {
      clientAssignmentsGridItem.onSuccess();
      isSuccess = true;
      this.fetchClients();
      this.headerService.reloadClientList();

      this.appNotificationService.crudNotify(
        CrudOperation.Create,
        this.clientTitle,
        isSuccess
      );
    });
  }

  update(
    clientAssignmentsGridItem: GridItemUpdateEvent<ClientWithAssignments>
  ): void {
    let isSuccess = false;

    this.clientService.update(clientAssignmentsGridItem.item).subscribe(() => {
      this.headerService.reloadClientList();
      clientAssignmentsGridItem.onSuccess();
      isSuccess = true;
      this.fetchClients();

      this.appNotificationService.crudNotify(
        CrudOperation.Update,
        this.clientTitle,
        isSuccess
      );
    });
  }

  assignUser(
    clientAssignmentsGridItem: GridItemUpdateEvent<ClientWithAssignments>
  ): void {
    const sourceKeys = clientAssignmentsGridItem.prevItem.clientAssignments.map(
      x => x.id
    );
    const targetKeys = clientAssignmentsGridItem.item.clientAssignments.map(
      x => x.id
    );

    const addedAssignments = difference(targetKeys, sourceKeys);
    const removedAssignments = difference(sourceKeys, targetKeys);

    for (const addedAssignment of addedAssignments) {
      this.clientService
        .addClientAssignment(
          clientAssignmentsGridItem.item!.clientId,
          addedAssignment
        )
        .subscribe();
    }

    for (const userRoleId of removedAssignments) {
      this.clientService
        .removeClientAssignment(
          clientAssignmentsGridItem.item!.clientId,
          userRoleId
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }

    clientAssignmentsGridItem.onSuccess();
    this.fetchClients();

    this.appNotificationService.crudNotify(
      CrudOperation.Update,
      this.clientTitle,
      true
    );
  }

  assignCountry(
    clientAssignmentsGridItem: GridItemUpdateEvent<ClientWithAssignments>
  ): void {
    const sourceKeys =
      clientAssignmentsGridItem.prevItem.countryAssignments.map(
        x => x.countryId
      );
    const targetKeys = clientAssignmentsGridItem.item.countryAssignments.map(
      x => x.countryId
    );

    const addedAssignments = difference(targetKeys, sourceKeys);
    const removedAssignments = difference(sourceKeys, targetKeys);

    const addAssignments$ = from(addedAssignments).pipe(
      mergeMap(addedAssignment =>
        this.clientService
          .addCountryMaterial(
            clientAssignmentsGridItem.item!.clientId,
            addedAssignment
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
      ),
      toArray()
    );

    const removeAssignments$ = from(removedAssignments).pipe(
      mergeMap(countryId =>
        this.clientService
          .removeCountryMaterial(
            clientAssignmentsGridItem.item!.clientId,
            countryId
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
      ),
      toArray()
    );

    forkJoin([addAssignments$, removeAssignments$]).subscribe(() => {
      this.fetchCountries();

      this.headerService.updatedClient.set(true);
      this.headerService.reloadClientList();
      this.notificationPanelService.reloadPendingApprovalCounter();
      this.notificationPanelService.reloadApprovedCounter();

      this.appNotificationService.crudNotify(
        CrudOperation.Update,
        this.clientTitle,
        true
      );
    });
    clientAssignmentsGridItem.onSuccess();
  }

  queryUpdate(clientQueryOptions: Partial<QueryOptions<Client>>): void {
    this.query = clientQueryOptions;
    this.fetchClients();
  }

  onDeleteClientRequested(client: Client): void {
    forkJoin({
      users: this.clientService.getClientAssignment(client.clientId),
      countries: this.clientService.getCountryAssignment(client.clientId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ users, countries }) => {
          const userCount = users.length;
          const countryCount = countries.length;
          if (userCount > 0 || countryCount > 0) {
            this.openClientDeleteBlockedModal(client, userCount, countryCount);
            return;
          }
          this.openDeleteClientConfirmModal(client);
        },
      });
  }

  private openDeleteClientConfirmModal(client: Client): void {
    this.modalService.open(
      ConfirmationModalComponent,
      { appModalId: 'client-delete-confirm-modal' },
      {
        title: this.translateService.instant('admin.modal.confirmationTitle'),
        content: this.translateService.instant(
          'admin.client.deleteClientConfirmMessage',
          { clientName: client.name }
        ),
        modalId: 'client-delete-confirm-modal',
        confirmationFn: () => this.confirmDeleteClient(client),
      }
    );
  }

  private openClientDeleteBlockedModal(
    client: Client,
    userCount: number,
    countryCount: number
  ): void {
    const reasons: string[] = [];
    if (userCount > 0) {
      reasons.push(
        this.translateService.instant('admin.client.deleteClientBlockedUsers', {
          count: userCount,
        })
      );
    }
    if (countryCount > 0) {
      reasons.push(
        this.translateService.instant(
          'admin.client.deleteClientBlockedCountries',
          { count: countryCount }
        )
      );
    }
    const content = this.translateService.instant(
      'admin.client.deleteClientBlockedMessage',
      {
        clientName: client.name,
        detail: reasons.join(
          this.translateService.instant(
            'admin.client.deleteClientBlockedJoiner'
          )
        ),
      }
    );
    this.modalService.open(
      ConfirmationModalComponent,
      { appModalId: 'client-delete-blocked-modal' },
      {
        title: this.translateService.instant(
          'admin.client.deleteClientBlockedTitle'
        ),
        content,
        modalId: 'client-delete-blocked-modal',
        hidePrimaryButton: true,
      }
    );
  }

  private confirmDeleteClient(client: Client): void {
    this.clientService
      .delete(client.clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.fetchClients();
          this.headerService.reloadClientList();
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.clientTitle,
            true
          );
        },
        error: () => {
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.clientTitle,
            false
          );
        },
      });
  }

  private fetchClients(): void {
    this.clientService
      .get(this.query)
      .subscribe((clientCollection: Collection<Client>) => {
        this.clients.set(clientCollection);
        if (
          !clientCollection.items.some(
            (client: Client) =>
              this.headerService.selectedClient()?.id === client.clientId &&
              client.isActive
          )
        ) {
          this.headerService.clearSelection();
        }
        this.headerService.reloadClientList();
      });
  }

  private fetchCountries(): void {
    const selectedClient = this.headerService.selectedClient();
    this.headerService.selectedClient.set(undefined);
    this.headerService.updatedClient.set(true);
    this.headerService.reloadClientList();
    this.headerService.setSelectedClient(selectedClient);
  }
}
